import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Boxes, Search, Package, AlertTriangle, Lock, ChevronDown, ChevronRight, 
  ArrowUpCircle, ArrowDownCircle, Calendar, FileText, User, Info, X, Download,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DepotStock = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [stokKartlari, setStokKartlari] = useState([]);
  const [stokDurum, setStokDurum] = useState({}); // Ürün adına göre stok durumu
  const [stokGirisleri, setStokGirisleri] = useState([]);
  const [stokCikislari, setStokCikislari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // Bilgilendirme kutusu state'i
  const [showStokInfo, setShowStokInfo] = useState(() => {
    return localStorage.getItem('depot_stok_info_hidden') !== 'true';
  });

  // Şifre Doğrulama State
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const EDIT_DELETE_PASSWORD = 'Alyatugba21';

  // Expanded rows state - giriş/çıkış hareketleri için
  const [expandedRows, setExpandedRows] = useState({});
  const [productMovements, setProductMovements] = useState({});
  const [loadingMovements, setLoadingMovements] = useState({});

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  // Şifre ile işlem başlatma
  const requestPasswordForAction = (action, data) => {
    setPendingAction({ action, data });
    setPasswordInput('');
    setPasswordModal(true);
  };

  // Şifre doğrulama ve işlem yürütme
  const handlePasswordConfirm = () => {
    if (passwordInput !== EDIT_DELETE_PASSWORD) {
      toast.error('Şifre yanlış!');
      return;
    }
    
    setPasswordModal(false);
    setPasswordInput('');
    
    if (pendingAction) {
      const { action, data } = pendingAction;
      switch (action) {
        case 'deleteStok':
          executeDeleteStok(data);
          break;
        default:
          break;
      }
      setPendingAction(null);
    }
  };

  // Kullanıcının şube ID'si
  const userBranchId = user?.branch_id;
  const userBranchName = user?.branch_name || 'Depo';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Stok kartları ve stok girişleri/çıkışları çek
      // branchIdOverride varsa query parametresi olarak ekle
      const branchParam = branchIdOverride ? `?branch_id=${branchIdOverride}` : '';
      const [kartRes, girisRes, cikisRes] = await Promise.all([
        axios.get(`${API}/api/depot/stok-kartlari${branchParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/depot/stok-girisleri${branchParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/depot/stok-cikisleri${branchParam}`, { headers: getAuthHeaders() })
      ]);
      
      setStokKartlari(kartRes.data);
      setStokGirisleri(girisRes.data);
      setStokCikislari(cikisRes.data);
      
      // Stok durumunu hesapla - SADECE GİRİŞ VE ÇIKIŞLARDAN
      // Mevcut Stok = Toplam Giriş - Toplam Çıkış
      const stokMap = {};
      
      // Girişlerden toplam giriş hesapla
      girisRes.data.forEach(giris => {
        giris.items?.forEach(item => {
          const productName = (item.product_name || '').toLowerCase().trim();
          if (productName) {
            if (!stokMap[productName]) {
              stokMap[productName] = { total_giris: 0, total_cikis: 0 };
            }
            stokMap[productName].total_giris += parseFloat(item.quantity || 0);
          }
        });
      });
      
      // Çıkışlardan toplam çıkış hesapla
      cikisRes.data.forEach(cikis => {
        cikis.items?.forEach(item => {
          const productName = (item.product_name || '').toLowerCase().trim();
          if (productName) {
            if (!stokMap[productName]) {
              stokMap[productName] = { total_giris: 0, total_cikis: 0 };
            }
            stokMap[productName].total_cikis += parseFloat(item.quantity || 0);
          }
        });
      });
      
      setStokDurum(stokMap);
      
    } catch (err) {
      console.error('Stok yüklenemedi:', err);
      setError('Stok bilgileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [branchIdOverride]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Silme işlemi
  const handleDeleteStok = (id) => {
    requestPasswordForAction('deleteStok', id);
  };

  const executeDeleteStok = async (id) => {
    try {
      await axios.delete(`${API}/api/depot/stok/${id}`, { headers: getAuthHeaders() });
      toast.success('Stok kaydı silindi');
      fetchData();
    } catch (err) {
      toast.error('Stok kaydı silinemedi');
    }
  };

  // Toggle row expansion and calculate movements
  const toggleRowExpansion = (productName) => {
    const key = productName.toLowerCase().trim();
    
    if (expandedRows[key]) {
      setExpandedRows(prev => ({ ...prev, [key]: false }));
    } else {
      setExpandedRows(prev => ({ ...prev, [key]: true }));
      
      if (!productMovements[key]) {
        setLoadingMovements(prev => ({ ...prev, [key]: true }));
        
        const movements = [];
        
        // Giriş hareketlerini topla
        stokGirisleri.forEach(giris => {
          giris.items?.forEach(item => {
            const itemName = (item.product_name || '').toLowerCase().trim();
            if (itemName === key) {
              movements.push({
                id: giris.id + '-giris-' + item.product_name,
                type: 'giris',
                date: giris.entry_datetime || giris.created_at,
                quantity: parseFloat(item.quantity) || 0,
                unit: item.unit || 'adet',
                source: giris.source || giris.kaynak || 'Manuel Giriş',
                cari_hesap: giris.cari_hesap_name || giris.supplier_name || '-',
                description: giris.description || item.description || '',
                created_by: giris.created_by_name || '-',
                belgeler: giris.documents || giris.belgeler || []
              });
            }
          });
        });
        
        // Çıkış hareketlerini topla
        stokCikislari.forEach(cikis => {
          cikis.items?.forEach(item => {
            const itemName = (item.product_name || '').toLowerCase().trim();
            if (itemName === key) {
              movements.push({
                id: cikis.id + '-cikis-' + item.product_name,
                type: 'cikis',
                date: cikis.exit_datetime || cikis.created_at,
                quantity: parseFloat(item.quantity) || 0,
                unit: item.unit || 'adet',
                target: cikis.target || cikis.hedef || '-',
                description: cikis.description || item.description || '',
                created_by: cikis.created_by_name || '-',
                belgeler: cikis.documents || cikis.belgeler || []
              });
            }
          });
        });
        
        // Tarihe göre sırala (en yeniden en eskiye)
        movements.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setProductMovements(prev => ({ ...prev, [key]: movements }));
        setLoadingMovements(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  // Tarih formatla
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stok kartlarını zenginleştir (mevcut stok, giriş, çıkış ekle)
  const enrichedProducts = stokKartlari.map(kart => {
    const productKey = (kart.product_name || kart.name || '').toLowerCase().trim();
    const stokInfo = stokDurum[productKey] || { total_giris: 0, total_cikis: 0 };
    
    // Mevcut stok = Toplam Giriş - Toplam Çıkış
    const mevcutStok = stokInfo.total_giris - stokInfo.total_cikis;
    
    return {
      id: kart.id,
      product_name: kart.product_name || kart.name,
      unit: kart.unit || kart.birim || 'adet',
      minimum_stock: kart.minimum_stock || kart.min_stok || 0,
      max_usage: kart.max_usage || kart.max_kullanim || 0,
      model: kart.model || '',
      features: kart.features || kart.ozellikler || '',
      mevcut_stok: mevcutStok,
      total_giris: stokInfo.total_giris,
      total_cikis: stokInfo.total_cikis,
      created_at: kart.created_at
    };
  });

  // Arama filtresi
  const filteredProducts = enrichedProducts.filter(p => 
    (p.product_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.model || '').toLowerCase().includes(search.toLowerCase())
  );

  // İstatistikler
  const totalItems = enrichedProducts.length;
  const lowStockItems = enrichedProducts.filter(p => {
    const minStock = p.minimum_stock || 0;
    return minStock > 0 && p.mevcut_stok > 0 && p.mevcut_stok <= minStock;
  }).length;
  const outOfStockItems = enrichedProducts.filter(p => p.mevcut_stok <= 0).length;
  const inStockItems = enrichedProducts.filter(p => p.mevcut_stok > 0).length;

  // Stok durumu badge
  const getStockStatus = (mevcutStok, minimumStock) => {
    const minStock = minimumStock || 0;
    if (mevcutStok <= 0) {
      return { label: 'Stok Yok', variant: 'destructive', className: 'bg-gray-100 text-gray-600 hover:bg-gray-100' };
    }
    if (minStock > 0 && mevcutStok <= minStock) {
      return { label: 'Kritik', variant: 'default', className: 'bg-red-100 text-red-700 hover:bg-red-100' };
    }
    if (minStock > 0 && mevcutStok <= minStock * 1.5) {
      return { label: 'Azalıyor', variant: 'default', className: 'bg-amber-100 text-amber-700 hover:bg-amber-100' };
    }
    return { label: 'Yeterli', variant: 'default', className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' };
  };

  // Excel Export
  const exportStokToExcel = () => {
    if (filteredProducts.length === 0) {
      toast.error('İndirilecek veri bulunmuyor');
      return;
    }

    const rows = filteredProducts.map(item => ({
      urunAdi: item.product_name || '-',
      model: item.model || '-',
      birim: item.unit || 'adet',
      mevcut: item.mevcut_stok || 0,
      toplamGiris: item.total_giris || 0,
      maxAlim: item.max_usage || '-',
      toplamCikis: item.total_cikis || 0,
      minStok: item.minimum_stock || '-',
      durum: (item.mevcut_stok <= 0) ? 'Stok Yok' : 
             (item.minimum_stock && item.mevcut_stok <= item.minimum_stock) ? 'Kritik' : 'Normal'
    }));

    const BOM = '\uFEFF';
    const headers = ['Ürün Adı', 'Model', 'Birim', 'Mevcut', 'Toplam Giriş', 'Max Alım', 'Toplam Çıkış', 'Min. Stok', 'Durum'];
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => [
        row.urunAdi, row.model, row.birim, row.mevcut, row.toplamGiris, row.maxAlim, row.toplamCikis, row.minStok, row.durum
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Depo_Stok_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Excel dosyası indirildi');
  };

  // Bilgilendirme kutusunu kapat
  const hideStokInfo = () => {
    setShowStokInfo(false);
    localStorage.setItem('depot_stok_info_hidden', 'true');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="depot-stock-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Depo Stok</h1>
            <p className="text-gray-500">Stok kartları ve hareketler</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Card>
          <CardContent className="py-6">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="depot-stock-error">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Depo Stok</h1>
            <p className="text-gray-500">Stok kartları ve hareketler</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-red-500">
              <Boxes className="h-12 w-12 mb-4" />
              <p>{error}</p>
              <button 
                onClick={fetchData}
                className="mt-4 text-emerald-600 hover:text-emerald-700 underline"
              >
                Tekrar Dene
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="depot-stock">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {userBranchName} - Depo Stok
          </h1>
          <p className="text-gray-500">
            Stok kartları ve hareketler {userBranchId ? '' : '(Tüm Şubeler)'}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Boxes className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam Ürün</p>
                <p className="text-2xl font-bold">{totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-emerald-100">
                <Package className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Stokta Olan</p>
                <p className="text-2xl font-bold text-emerald-600">{inStockItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-100">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Kritik Seviye</p>
                <p className="text-2xl font-bold text-amber-600">{lowStockItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-gray-100">
                <Minus className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Stok Yok</p>
                <p className="text-2xl font-bold text-gray-600">{outOfStockItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5 text-emerald-600" />
              Stok Kartları Listesi
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportStokToExcel}
                className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              >
                <Download className="h-4 w-4" />
                Excel İndir
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Ürün veya model ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="stock-search-input"
                />
              </div>
            </div>
          </div>
          
          {showStokInfo && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
              <button 
                onClick={hideStokInfo}
                className="absolute top-2 right-2 p-1 hover:bg-blue-100 rounded-full transition-colors"
                aria-label="Bilgilendirmeyi kapat"
              >
                <X className="h-4 w-4 text-blue-600" />
              </button>
              <div className="flex gap-3 pr-6">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  <strong>Mevcut Stok = Toplam Giriş - Toplam Çıkış</strong> olarak hesaplanır. 
                  Her ürüne tıklayarak giriş ve çıkış hareketlerini görebilirsiniz.
                  {!userBranchId && ' (Tüm şubelerin verileri gösteriliyor)'}
                </p>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Boxes className="h-12 w-12 mb-4 text-gray-300" />
              <p>Stok kartı bulunamadı</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Ürün Adı</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-center">Birim</TableHead>
                  <TableHead className="text-right">Mevcut Stok</TableHead>
                  <TableHead className="text-right">Toplam Giriş</TableHead>
                  <TableHead className="text-right">Toplam Çıkış</TableHead>
                  <TableHead className="text-right">Min. Stok</TableHead>
                  <TableHead className="text-right">Max Alım</TableHead>
                  <TableHead className="text-center">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((item) => {
                  const status = getStockStatus(item.mevcut_stok, item.minimum_stock);
                  const productKey = (item.product_name || '').toLowerCase().trim();
                  const isExpanded = expandedRows[productKey];
                  const movements = productMovements[productKey] || [];
                  const isLoadingMovements = loadingMovements[productKey];
                  const hasStock = item.mevcut_stok > 0 || item.total_giris > 0 || item.total_cikis > 0;
                  
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow 
                        data-testid={`stock-row-${item.id}`}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                          isExpanded ? 'bg-emerald-50' : hasStock ? '' : 'bg-gray-50/50'
                        }`}
                        onClick={() => toggleRowExpansion(item.product_name)}
                      >
                        <TableCell className="w-10">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Package className={`h-4 w-4 ${hasStock ? 'text-emerald-500' : 'text-gray-400'}`} />
                            {item.product_name}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.model || '-'}
                        </TableCell>
                        <TableCell className="text-center">{item.unit}</TableCell>
                        <TableCell className={`text-right font-mono text-lg font-semibold ${
                          item.mevcut_stok > 0 ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {item.mevcut_stok || 0}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {item.total_giris > 0 ? `+${item.total_giris}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-500">
                          {item.total_cikis > 0 ? `-${item.total_cikis}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-gray-500">
                          {item.minimum_stock || '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-blue-600">
                          {item.max_usage || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={status.variant} 
                            className={status.className}
                          >
                            {status.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded - Stok Hareketleri */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={10} className="bg-gray-50 p-0">
                            <div className="p-4 space-y-4">
                              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                <FileText className="h-4 w-4" />
                                {item.product_name} - Stok Hareketleri
                              </div>
                              
                              {isLoadingMovements ? (
                                <div className="space-y-2">
                                  <Skeleton className="h-10 w-full" />
                                  <Skeleton className="h-10 w-full" />
                                  <Skeleton className="h-10 w-full" />
                                </div>
                              ) : movements.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                  <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                  <p>Bu ürün için henüz giriş/çıkış hareketi yok</p>
                                </div>
                              ) : (
                                <div className="border rounded-lg overflow-hidden">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-gray-100">
                                        <TableHead className="text-xs">Tarih</TableHead>
                                        <TableHead className="text-xs">Hareket</TableHead>
                                        <TableHead className="text-xs text-right">Miktar</TableHead>
                                        <TableHead className="text-xs">Kaynak/Hedef</TableHead>
                                        <TableHead className="text-xs">Açıklama</TableHead>
                                        <TableHead className="text-xs">İşlemi Yapan</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {movements.map((mov, idx) => (
                                        <TableRow key={mov.id || idx} className="hover:bg-gray-50">
                                          <TableCell className="text-sm">
                                            <div className="flex items-center gap-1">
                                              <Calendar className="h-3 w-3 text-gray-400" />
                                              {formatDate(mov.date)}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            {mov.type === 'giris' ? (
                                              <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                                                <TrendingUp className="h-3 w-3" />
                                                Giriş
                                              </Badge>
                                            ) : (
                                              <Badge className="bg-red-100 text-red-700 gap-1">
                                                <TrendingDown className="h-3 w-3" />
                                                Çıkış
                                              </Badge>
                                            )}
                                          </TableCell>
                                          <TableCell className={`text-right font-mono font-semibold ${
                                            mov.type === 'giris' ? 'text-emerald-600' : 'text-red-600'
                                          }`}>
                                            {mov.type === 'giris' ? '+' : '-'}{mov.quantity} {mov.unit}
                                          </TableCell>
                                          <TableCell className="text-sm">
                                            {mov.type === 'giris' ? (
                                              <span className="text-blue-600">{mov.cari_hesap || mov.source}</span>
                                            ) : (
                                              <span className="text-purple-600">{mov.target}</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-sm text-gray-600 max-w-[200px] truncate" title={mov.description}>
                                            {mov.description || '-'}
                                          </TableCell>
                                          <TableCell className="text-sm">
                                            <div className="flex items-center gap-1 text-gray-500">
                                              <User className="h-3 w-3" />
                                              {mov.created_by}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                              
                              {/* Özet */}
                              {movements.length > 0 && (
                                <div className="flex gap-6 text-sm pt-2 border-t">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Toplam Hareket:</span>
                                    <span className="font-semibold">{movements.length}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                                    <span className="text-gray-500">Giriş:</span>
                                    <span className="font-semibold text-emerald-600">
                                      {movements.filter(m => m.type === 'giris').reduce((sum, m) => sum + m.quantity, 0)} {item.unit}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <TrendingDown className="h-4 w-4 text-red-500" />
                                    <span className="text-gray-500">Çıkış:</span>
                                    <span className="font-semibold text-red-600">
                                      {movements.filter(m => m.type === 'cikis').reduce((sum, m) => sum + m.quantity, 0)} {item.unit}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Şifre Doğrulama Modalı */}
      <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              Şifre Doğrulama
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Bu işlemi gerçekleştirmek için şifre girmeniz gerekmektedir.
            </p>
            <div>
              <Label>Şifre *</Label>
              <Input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Şifreyi girin..."
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordConfirm()}
                data-testid="password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordModal(false)}>İptal</Button>
            <Button onClick={handlePasswordConfirm} className="bg-amber-600 hover:bg-amber-700" data-testid="password-confirm-btn">
              Onayla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotStock;
